/**
 * heldSectionInserts — sections drafted but deliberately NOT yet in the live doc.
 *
 * A section whose ``section_ready`` says ``awaiting_audit`` is held here until
 * its regulatory audit settles, then inserted ONCE with the audit's flags
 * already in place — instead of inserting the bare draft and rewriting the
 * whole section a minute later when the audit lands (the "keeps getting added
 * and re-added" bug).
 *
 * Only ``section_final`` (the shell) releases a held section. The server sends
 * it after the audit's annotated draft is saved, for every audit outcome —
 * including a skip because the audit library isn't built yet, in which case
 * the draft goes in unaudited. Run-level terminals release everything still
 * held, so nothing is stranded.
 *
 * reviewStore's backend ``audit_complete`` path does NOT write the section
 * itself when the shell owns it (held, inserting, or just inserted): it waits
 * on {@link awaitLiveSectionInsert} so its "running" latch lasts until the
 * markers are in the doc. When both paths wrote, the section went into Word
 * twice (prod run 6ac628c8).
 *
 * Module-level (not React state) so reviewStore and the shell share it.
 */

type HeldInsert = () => Promise<void>;

const held = new Map<string, HeldInsert>();
/** Sections released in the current regen pass — ``regen_complete`` uses this
 *  to skip its own re-projection when the audit already inserted the section. */
const released = new Set<string>();
/** Released inserts still writing into Word. */
const inflight = new Map<string, Promise<void>>();
/** Waiters on a held section's eventual insert (see awaitLiveSectionInsert). */
const waiters = new Map<string, Array<() => void>>();

function settleWaiters(k: string): void {
	const ws = waiters.get(k);
	if (!ws) return;
	waiters.delete(k);
	for (const w of ws) w();
}

function key(runId: string, moduleId: string): string {
	return `${runId}:${moduleId}`;
}

/** Hold a section's live insert until {@link releaseHeldSectionInsert}. A later
 *  hold for the same section replaces the earlier one (latest draft wins). */
export function holdSectionInsert(runId: string, moduleId: string, insert: HeldInsert): void {
	held.set(key(runId, moduleId), insert);
	released.delete(key(runId, moduleId));
}

export function isSectionInsertHeld(runId: string, moduleId: string): boolean {
	return held.has(key(runId, moduleId));
}

/** Run a held section's insert (once) and return its promise, or ``null`` when
 *  nothing was held for it — the caller then does whatever it did before. */
export function releaseHeldSectionInsert(runId: string, moduleId: string): Promise<void> | null {
	const k = key(runId, moduleId);
	const insert = held.get(k);
	if (!insert) return null;
	held.delete(k);
	released.add(k);
	const run = insert().finally(() => {
		if (inflight.get(k) === run) inflight.delete(k);
		settleWaiters(k);
	});
	inflight.set(k, run);
	return run;
}

/** Forget a held insert without running it (a stopped regen restored the old
 *  draft; the writer loaded the section by hand). Returns whether one was held. */
export function dropHeldSectionInsert(runId: string, moduleId: string): boolean {
	const k = key(runId, moduleId);
	const had = held.delete(k);
	if (had) settleWaiters(k);
	return had;
}

/**
 * When the shell owns this section's live insert, a promise that resolves once
 * that insert has finished — immediately if it already went in since the
 * section was last held. ``null`` when the shell doesn't own it, so the caller
 * writes the section itself. Consumes the "just inserted" mark, so only the
 * first backend audit after an insert defers to it.
 */
export function awaitLiveSectionInsert(runId: string, moduleId: string): Promise<void> | null {
	const k = key(runId, moduleId);
	if (held.has(k)) {
		return new Promise<void>((resolve) => {
			const ws = waiters.get(k) ?? [];
			ws.push(resolve);
			waiters.set(k, ws);
		});
	}
	const running = inflight.get(k);
	if (running) {
		released.delete(k);
		return running.catch(() => undefined);
	}
	if (released.delete(k)) return Promise.resolve();
	return null;
}

/** A NEW audit started on this section (e.g. the catch-up sweep once the
 *  audit library is ready): an earlier insert no longer covers its result. */
export function forgetReleasedSectionInsert(runId: string, moduleId: string): void {
	released.delete(key(runId, moduleId));
}

/** True (once) when this section was released since it was last held. */
export function consumeReleasedSectionInsert(runId: string, moduleId: string): boolean {
	return released.delete(key(runId, moduleId));
}

/** Release every section still held for ``runId`` — a run-level terminal
 *  (complete / cancelled / error / paused) must never strand a draft. */
export function releaseAllHeldSectionInserts(runId: string): void {
	for (const k of [...held.keys()]) {
		if (k.startsWith(`${runId}:`)) void releaseHeldSectionInsert(runId, k.slice(runId.length + 1));
	}
}

/** Test hook. */
export function __resetHeldSectionInsertsForTests(): void {
	held.clear();
	released.clear();
	inflight.clear();
	waiters.clear();
}
